import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api';
import { Badge, ActivityFeed, Money, Modal, TaskList } from '../components/ui';
import { AccountForm } from './Accounts';
import { ContactForm } from './Contacts';
import { useAuth } from '../context/AuthContext';
import { fmtDate } from '../utils';

// Quick-add opportunity form (minimal fields, pre-filled with account)
function QuickOppForm({ accountId, onSave, onClose }) {
  const [form, setForm] = useState({ name:'', stage:'Prospecting', priority:'Medium', forecasted_tcv:'', expected_close_date:'' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const handleSave = async () => {
    if (!form.name.trim()) return setError('Name required');
    setSaving(true);
    try {
      await api.post('/opportunities', { ...form, account_id: accountId });
      onSave();
    } catch(e) { setError(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Add Opportunity" onClose={onClose}>
      <div className="modal-body">
        {error && <div className="error-msg">{error}</div>}
        <div className="form-group"><label className="form-label">Name *</label><input className="form-input" value={form.name} onChange={set('name')} autoFocus /></div>
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Stage</label>
            <select className="form-select" value={form.stage} onChange={set('stage')}>
              {['Prospecting','Qualification','Shaping','Pre-RFP','Proposal','Evaluation'].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="form-group"><label className="form-label">Priority</label>
            <select className="form-select" value={form.priority} onChange={set('priority')}>
              {['Critical','High','Medium','Low'].map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
        </div>
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Forecasted TCV ($)</label><input className="form-input" type="number" value={form.forecasted_tcv} onChange={set('forecasted_tcv')} /></div>
          <div className="form-group"><label className="form-label">Close Date</label><input className="form-input" type="date" value={form.expected_close_date} onChange={set('expected_close_date')} /></div>
        </div>
      </div>
      <div className="modal-footer">
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Add Opportunity'}</button>
      </div>
    </Modal>
  );
}

// Quick-add contract form
function QuickContractForm({ accountId, onSave, onClose }) {
  const [form, setForm] = useState({ name:'', contract_type:'FFP', role:'Prime', status:'draft', value:'', start_date:'', end_date:'' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const handleSave = async () => {
    if (!form.name.trim()) return setError('Name required');
    setSaving(true);
    try {
      await api.post('/contracts', { ...form, account_id: accountId });
      onSave();
    } catch(e) { setError(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Add Contract" onClose={onClose}>
      <div className="modal-body">
        {error && <div className="error-msg">{error}</div>}
        <div className="form-group"><label className="form-label">Name *</label><input className="form-input" value={form.name} onChange={set('name')} autoFocus /></div>
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Type</label>
            <select className="form-select" value={form.contract_type} onChange={set('contract_type')}>
              {['FFP','T&M','CPFF','IDIQ','BPA'].map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="form-group"><label className="form-label">Role</label>
            <select className="form-select" value={form.role} onChange={set('role')}>
              {['Prime','Subcontractor','Teaming Partner'].map(r => <option key={r}>{r}</option>)}
            </select>
          </div>
        </div>
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Value ($)</label><input className="form-input" type="number" value={form.value} onChange={set('value')} /></div>
          <div className="form-group"><label className="form-label">Status</label>
            <select className="form-select" value={form.status} onChange={set('status')}>
              {['draft','active','expired'].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Start Date</label><input className="form-input" type="date" value={form.start_date} onChange={set('start_date')} /></div>
          <div className="form-group"><label className="form-label">End Date</label><input className="form-input" type="date" value={form.end_date} onChange={set('end_date')} /></div>
        </div>
      </div>
      <div className="modal-footer">
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Add Contract'}</button>
      </div>
    </Modal>
  );
}

export default function AccountDetail() {
  const { id } = useParams(); const navigate = useNavigate(); const { can } = useAuth();
  const [account, setAccount] = useState(null); const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [showEdit, setShowEdit] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [showAddOpp, setShowAddOpp] = useState(false);
  const [showAddContract, setShowAddContract] = useState(false);
  const [users, setUsers] = useState([]);

  const load = () => api.get(`/accounts/${id}`).then(r => setAccount(r.data)).catch(() => navigate('/accounts')).finally(() => setLoading(false));
  useEffect(() => { load(); api.get('/auth/users').then(r => setUsers(r.data)).catch(() => {}); }, [id]);

  if (loading) return <div style={{ padding:40,color:'var(--gray-400)' }}>Loading...</div>;
  if (!account) return null;

  const tabs = ['overview','contacts','opportunities','contracts','tasks','activity'];

  const tabAddBtn = (label, action) => can('edit') && (
    <button className="btn btn-primary btn-sm" onClick={action}>+ Add {label}</button>
  );

  return (
    <>
      <div className="page-header">
        <div>
          <Link to="/accounts" className="detail-back">← Accounts</Link>
          <div style={{ display:'flex',alignItems:'center',gap:12 }}>
            <span className="page-title">{account.name}</span>
            <Badge status={account.status} />
            <Badge status={account.customer_type} />
          </div>
          {account.office_division && <div style={{ fontSize:13,color:'var(--gray-500)',marginTop:2 }}>{account.office_division}</div>}
        </div>
        {can('edit') && <button className="btn btn-secondary" onClick={() => setShowEdit(true)}>✏️ Edit</button>}
      </div>
      <div className="page-body">
        <div style={{ display:'flex',gap:2,marginBottom:20,borderBottom:'1px solid var(--gray-200)' }}>
          {tabs.map(t => (
            <button key={t} onClick={() => setTab(t)} style={{ padding:'8px 16px',background:'none',border:'none',borderBottom:tab===t?'2px solid var(--primary)':'2px solid transparent',color:tab===t?'var(--primary)':'var(--gray-500)',fontWeight:tab===t?600:400,fontSize:13,cursor:'pointer',textTransform:'capitalize',marginBottom:-1 }}>{t}</button>
          ))}
        </div>

        {tab === 'overview' && (
          <div className="detail-grid">
            <div className="card">
              <div className="card-header"><span className="card-title">Account Details</span></div>
              <div className="card-body">
                <div className="info-grid">
                  {[
                    ['Customer Type', <Badge status={account.customer_type} />],
                    ['Office / Division', account.office_division],
                    account.customer_type === 'Commercial' && ['Industry', account.industry],
                    account.customer_type === 'Commercial' && ['Annual Revenue', account.annual_revenue],
                    ['Website', account.website ? <a href={account.website} target="_blank" rel="noreferrer">{account.website}</a> : null],
                    ['Phone', account.phone],
                    !['Federal'].includes(account.customer_type) && ['Location', [account.city,account.state].filter(Boolean).join(', ')],
                    ['Owner', account.owner_name],
                    ['Created', fmtDate(account.created_at)],
                  ].filter(Boolean).map(([label, val]) => (
                    <div key={label}><div className="detail-label">{label}</div><div className="detail-value">{val||'—'}</div></div>
                  ))}
                </div>
                {account.notes && <><hr className="divider" /><div className="detail-label">Notes</div><div className="detail-value" style={{ marginTop:4,whiteSpace:'pre-wrap' }}>{account.notes}</div></>}
              </div>
            </div>
            <div className="card"><ActivityFeed relatedType="account" relatedId={id} activities={account.activities||[]} canContribute={can('contribute')} /></div>
          </div>
        )}

        {tab === 'contacts' && (
          <div className="card">
            <div className="card-header">
              <span className="card-title">Contacts ({account.contacts?.length||0})</span>
              {tabAddBtn('Contact', () => setShowAddContact(true))}
            </div>
            <table><thead><tr><th>Name</th><th>Title</th><th>Email</th><th>Relationship</th></tr></thead>
              <tbody>
                {account.contacts?.length === 0 && <tr><td colSpan={4}><div className="empty-state"><div className="empty-state-icon">👤</div><div className="empty-state-text">No contacts yet</div></div></td></tr>}
                {account.contacts?.map(c => (
                  <tr key={c.id} onClick={() => navigate(`/contacts/${c.id}`)}>
                    <td><span className="td-link">{c.first_name} {c.last_name}</span></td>
                    <td>{c.title||'—'}</td><td>{c.email||'—'}</td>
                    <td><Badge status={c.relationship_strength} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'opportunities' && (
          <div className="card">
            <div className="card-header">
              <span className="card-title">Opportunities ({account.opportunities?.length||0})</span>
              {tabAddBtn('Opportunity', () => setShowAddOpp(true))}
            </div>
            <table><thead><tr><th>ID</th><th>Name</th><th>Stage</th><th>Priority</th><th>TCV</th><th>Close Date</th></tr></thead>
              <tbody>
                {account.opportunities?.length === 0 && <tr><td colSpan={6}><div className="empty-state"><div className="empty-state-icon">💼</div><div className="empty-state-text">No opportunities yet</div></div></td></tr>}
                {account.opportunities?.map(o => (
                  <tr key={o.id} onClick={() => navigate(`/opportunities/${o.id}`)}>
                    <td style={{ fontFamily:'monospace',fontSize:12,color:'var(--gray-500)' }}>{o.opportunity_id}</td>
                    <td><span className="td-link">{o.name}</span></td>
                    <td><Badge status={o.stage} /></td>
                    <td><Badge status={o.priority} /></td>
                    <td><Money value={o.forecasted_tcv} /></td>
                    <td>{fmtDate(o.expected_close_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'contracts' && (
          <div className="card">
            <div className="card-header">
              <span className="card-title">Contracts ({account.contracts?.length||0})</span>
              {tabAddBtn('Contract', () => setShowAddContract(true))}
            </div>
            <table><thead><tr><th>Number</th><th>Name</th><th>Status</th><th>Value</th><th>End Date</th></tr></thead>
              <tbody>
                {account.contracts?.length === 0 && <tr><td colSpan={5}><div className="empty-state"><div className="empty-state-icon">📄</div><div className="empty-state-text">No contracts yet</div></div></td></tr>}
                {account.contracts?.map(c => (
                  <tr key={c.id} onClick={() => navigate(`/contracts/${c.id}`)}>
                    <td style={{ fontFamily:'monospace',fontSize:12 }}>{c.contract_number}</td>
                    <td><span className="td-link">{c.name}</span></td>
                    <td><Badge status={c.status} /></td>
                    <td><Money value={c.value} /></td>
                    <td>{fmtDate(c.end_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'tasks' && (
          <div className="card">
            <TaskList relatedType="account" relatedId={id} tasks={account.tasks||[]} users={users} canContribute={can('contribute')} onUpdate={load} />
          </div>
        )}
        {tab === 'activity' && <div className="card"><ActivityFeed relatedType="account" relatedId={id} activities={account.activities||[]} canContribute={can('contribute')} /></div>}
      </div>

      {showEdit && <AccountForm account={account} onSave={() => { setShowEdit(false); load(); }} onClose={() => setShowEdit(false)} />}
      {showAddContact && <ContactForm contact={{ account_id: id }} onSave={() => { setShowAddContact(false); load(); }} onClose={() => setShowAddContact(false)} />}
      {showAddOpp && <QuickOppForm accountId={id} onSave={() => { setShowAddOpp(false); load(); }} onClose={() => setShowAddOpp(false)} />}
      {showAddContract && <QuickContractForm accountId={id} onSave={() => { setShowAddContract(false); load(); }} onClose={() => setShowAddContract(false)} />}
    </>
  );
}
